import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Row } from '@/lib/supabase'
import type { TodoList } from '@/lib/todos'

export type Todo = Omit<Row<'todos'>, 'list'> & { list: TodoList }

const todoKeys = { all: ['todos'] as const }

/** Her own lists only: RLS limits todos to the person signed in. */
export function useTodos() {
  return useQuery({
    queryKey: todoKeys.all,
    queryFn: async (): Promise<Todo[]> => {
      const { data, error } = await supabase.from('todos').select('*').order('sort_order')
      if (error) throw error
      return data as Todo[]
    },
  })
}

/**
 * Every change is optimistic: a tick or a drag that waits for the network
 * feels broken. A failure puts the lists back and says so.
 */
function useTodoMutation<V>(
  write: (variables: V) => Promise<void>,
  apply: (current: Todo[], variables: V) => Todo[],
  failure: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: write,
    onMutate: async (variables: V) => {
      await queryClient.cancelQueries({ queryKey: todoKeys.all })
      const previous = queryClient.getQueryData<Todo[]>(todoKeys.all)
      queryClient.setQueryData<Todo[]>(todoKeys.all, (current) => apply(current ?? [], variables))
      return { previous }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(todoKeys.all, context.previous)
      toast.error(failure, { description: describeError(error) })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: todoKeys.all })
    },
  })
}

export function useAddTodo() {
  return useTodoMutation<{ id: string; list: TodoList; title: string; sort_order: number }>(
    async (todo) => {
      const { error } = await supabase.from('todos').insert(todo)
      if (error) throw error
    },
    (current, todo) => [
      ...current,
      { ...todo, owner_id: '', done_at: null, created_at: new Date().toISOString() },
    ],
    'Could not add that',
  )
}

export function useUpdateTodo() {
  return useTodoMutation<{ id: string; patch: Partial<Pick<Todo, 'title' | 'done_at' | 'list' | 'sort_order'>> }>(
    async ({ id, patch }) => {
      const { error } = await supabase.from('todos').update(patch).eq('id', id)
      if (error) throw error
    },
    (current, { id, patch }) => current.map((todo) => (todo.id === id ? { ...todo, ...patch } : todo)),
    'Could not save that',
  )
}

export function useDeleteTodos() {
  return useTodoMutation<string[]>(
    async (ids) => {
      const { error } = await supabase.from('todos').delete().in('id', ids)
      if (error) throw error
    },
    (current, ids) => current.filter((todo) => !ids.includes(todo.id)),
    'Could not remove that',
  )
}

// ---------------------------------------------------------------------------
// Board settings: each board's background colour, per person (todo_boards).
// ---------------------------------------------------------------------------

const boardKeys = { all: ['todo-boards'] as const }

export function useTodoBoards() {
  return useQuery({
    queryKey: boardKeys.all,
    queryFn: async (): Promise<Record<string, string | null>> => {
      const { data, error } = await supabase.from('todo_boards').select('list, color')
      if (error) throw error
      return Object.fromEntries(data.map((row) => [row.list, row.color]))
    },
  })
}

export function useSetBoardColor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ list, color }: { list: TodoList; color: string | null }) => {
      const { error } = await supabase.from('todo_boards').upsert({ list, color }, { onConflict: 'owner_id,list' })
      if (error) throw error
    },
    onMutate: async ({ list, color }) => {
      await queryClient.cancelQueries({ queryKey: boardKeys.all })
      const previous = queryClient.getQueryData<Record<string, string | null>>(boardKeys.all)
      queryClient.setQueryData<Record<string, string | null>>(boardKeys.all, (current) => ({ ...current, [list]: color }))
      return { previous }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(boardKeys.all, context.previous)
      toast.error('Could not change the colour', { description: describeError(error) })
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: boardKeys.all }),
  })
}
